import { ClusterCluster } from '@clinic/client'
import envConfig from '~/utils/env-config'

import { ClusterDeployType, ClusterDeployTypeV2 } from './cluster-status'

const O11Y_LOKI_DATA_SOURCE = 'o11y-loki-data'

export function getLokiFullPath(
  dataSource: string,
  expr: string,
  from: number,
  to: number,
  queryParams?: Record<string, string | undefined>
) {
  let pathQuery = envConfig.lokiUrl.pathQueryTemplate
    .replace('{{DATASOURCE}}', dataSource) // dataSource: Loki-aws-us-west-2 (Loki-{provider}-{region})
    .replace('{{EXPR}}', expr)
    .replace('{{FROM}}', from + '') // from and to time unit is milliseconds
    .replace('{{TO}}', to + '')

  if (queryParams) {
    const params = new URLSearchParams()
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      }
    })
    const queryString = params.toString()
    if (queryString) {
      pathQuery += `${pathQuery.includes('?') ? '&' : '?'}${queryString}`
    }
  }

  return encodeURI(pathQuery)
}

export function getDedicatedInstanceLogPath(
  clusterInfo: ClusterCluster,
  timeRange: { start: number; end: number },
  container?: string,
  containerLike?: string
) {
  let containerExpr = ''
  if (container) {
    containerExpr = `, container=\\"${container}\\"`
  } else if (containerLike) {
    containerExpr = `, container=~\\".*${containerLike}.*\\"`
  }
  let expr = `{cluster_env=\\"${envConfig.appReleaseEnv}\\", namespace=\\"tidb${clusterInfo.id}\\"${containerExpr}} |= \`\``

  return getLokiFullPath(
    O11Y_LOKI_DATA_SOURCE,
    expr,
    timeRange.start * 1000,
    timeRange.end * 1000,
    getO11yLokiRouteParams(clusterInfo)
  )
}

export function getDedicatedDataPlaneLogPath(
  clusterInfo: ClusterCluster,
  timeRange: { start: number; end: number },
  namespace?: string,
  namespaceLike?: string,
  container?: string
) {
  let namespaceExpr = ''
  if (namespace) {
    namespaceExpr = `, namespace=\\"${namespace}\\"`
  } else if (namespaceLike) {
    namespaceExpr = `, namespace=~\\".*${namespaceLike}.*\\"`
  }
  let containerExpr = ''
  if (container) {
    containerExpr = `, container=\\"${container}\\"`
  }
  let expr = `{cluster_env=\\"${envConfig.appReleaseEnv}\\", project_id=\\"${clusterInfo.projectID}\\"${namespaceExpr}${containerExpr}} |= \`\``

  return getLokiFullPath(
    O11Y_LOKI_DATA_SOURCE,
    expr,
    timeRange.start * 1000,
    timeRange.end * 1000,
    getO11yLokiRouteParams(clusterInfo)
  )
}

export function getServerlessInstanceLogCluster(clusterInfo: ClusterCluster) {
  const { provider, region } = clusterInfo
  const shootName =
    clusterInfo.shootName ||
    `${region!}-${provider === 'alicloud' ? 'a01' : 'f01'}`
  let clusterName = `${envConfig.appReleaseEnv}-${shootName}`
  if (provider === 'alicloud') {
    clusterName = `${clusterName}-cluster`
  }
  return clusterName
}

export function getServerlessLokiDataSource() {
  return O11Y_LOKI_DATA_SOURCE
}

function getServerlessControlPlaneInfo(clusterInfo: ClusterCluster) {
  const { provider, region } = clusterInfo
  const projectID =
    clusterInfo.shootName ||
    `${region!}-${provider === 'alicloud' ? 'a01' : 'f01'}`

  return `${projectID}/${provider!}-${region!}/devtier-infra`
}

function getServerlessLokiRouteParams(clusterInfo: ClusterCluster) {
  return {
    'var-cluster_id': clusterInfo.id,
    'var-control_plane_info': getServerlessControlPlaneInfo(clusterInfo)
  }
}

function getO11yLokiRouteParams(clusterInfo: ClusterCluster) {
  return {
    'var-cluster_id': clusterInfo.id
  }
}

export function getServerlessComponentLogPath(
  clusterInfo: ClusterCluster,
  timeRange: { start: number; end: number },
  container: string,
  namespace?: string
) {
  const ns = namespace || 'tidb-serverless'
  const serverlessLogCluster = getServerlessInstanceLogCluster(clusterInfo)
  const expr = `{cluster=\\"${serverlessLogCluster}\\", namespace=\\"${ns}\\", container=\\"${container}\\"} |= \`\``
  const dataSource = getServerlessLokiDataSource()

  return getLokiFullPath(
    dataSource,
    expr,
    timeRange.start * 1000,
    timeRange.end * 1000,
    getServerlessLokiRouteParams(clusterInfo)
  )
}

export function getServerlessComponentLogPathWithNSRegex(
  clusterInfo: ClusterCluster,
  timeRange: { start: number; end: number },
  namespaceRegex: string,
  container: string
) {
  const serverlessLogCluster = getServerlessInstanceLogCluster(clusterInfo)
  const expr = `{cluster=\\"${serverlessLogCluster}\\", namespace=~\\"${namespaceRegex}\\", container=\\"${container}\\"} |= \`\``
  const dataSource = getServerlessLokiDataSource()

  return getLokiFullPath(
    dataSource,
    expr,
    timeRange.start * 1000,
    timeRange.end * 1000,
    getServerlessLokiRouteParams(clusterInfo)
  )
}

export function getServerlessComponentLogPathWithContainerRegex(
  clusterInfo: ClusterCluster,
  timeRange: { start: number; end: number },
  namespace: string,
  containerRegex: string
) {
  const serverlessLogCluster = getServerlessInstanceLogCluster(clusterInfo)
  const expr = `{cluster=\\"${serverlessLogCluster}\\", namespace=\\"${namespace}\\", container=~\\"${containerRegex}\\"} |= \`\``
  const dataSource = getServerlessLokiDataSource()

  return getLokiFullPath(
    dataSource,
    expr,
    timeRange.start * 1000,
    timeRange.end * 1000,
    getServerlessLokiRouteParams(clusterInfo)
  )
}

export function getPremiumInstanceLogPath(
  clusterInfo: ClusterCluster,
  timeRange: { start: number; end: number },
  component?: string,
  componentLike?: string,
  logicalClusterOnly?: boolean,
  clusterKeyword?: boolean,
  keyword?: string
) {
  // Premium clusters use their physical cluster (parentID) for logging
  // Similar to dedicated clusters but using parentID instead of cluster ID
  let namespace = `tidb${clusterInfo.parentID}`
  let tidbClusterIDExpr = ''
  const skipTidbClusterIdForTiProxy =
    clusterInfo.deployTypeV2 === ClusterDeployTypeV2.EssentialV2 &&
    component === 'tiproxy'

  if (
    clusterInfo.deployType === 'nextgen-host' ||
    clusterInfo.deployType === 'byoc-host'
  ) {
    namespace = `tidb${clusterInfo.id}`
  } else if (logicalClusterOnly && !skipTidbClusterIdForTiProxy) {
    tidbClusterIDExpr += `, tidb_cluster_id=\\"${clusterInfo.id}\\"`
  }
  let componentExpr = ''
  if (component) {
    componentExpr = `, component=\\"${component}\\"`
  } else if (componentLike) {
    componentExpr = `, component=~\\"${componentLike}\\"`
  }
  let clusterKeywordExpr = ''
  if (
    clusterKeyword &&
    (clusterInfo.deployType === 'premium' || clusterInfo.deployType === 'byoc')
  ) {
    clusterKeywordExpr = ` |= \`${clusterInfo.id}\``
  }
  let expr = `{cluster_env=\\"${
    envConfig.appReleaseEnv
  }\\", namespace=\\"${namespace}\\"${tidbClusterIDExpr}${componentExpr} }${clusterKeywordExpr} |= \`${
    keyword || ''
  }\``

  const isBYOC =
    clusterInfo.deployType === ClusterDeployType.Byoc ||
    clusterInfo.deployType === ClusterDeployType.ByocHost
  const byocDataSource = `Loki-${clusterInfo.provider}-${
    clusterInfo.shootName ? clusterInfo.shootName : clusterInfo.region
  }`
  const finalDataSource = isBYOC ? byocDataSource : O11Y_LOKI_DATA_SOURCE
  const queryParams = isBYOC ? undefined : getO11yLokiRouteParams(clusterInfo)
  return getLokiFullPath(
    finalDataSource,
    expr,
    timeRange.start * 1000,
    timeRange.end * 1000,
    queryParams
  )
}

export function getPremiumRestoreLogPath(
  clusterInfo: ClusterCluster,
  timeRange: { start: number; end: number }
) {
  const expr = `{cluster_env=\\"${envConfig.appReleaseEnv}\\", namespace=\\"tidb${clusterInfo.parentID}\\", component=\\"restore\\", cluster_id=\\"${clusterInfo.id}\\"} |= \`cl-restore-${clusterInfo.id}\``

  return getLokiFullPath(
    O11Y_LOKI_DATA_SOURCE,
    expr,
    timeRange.start * 1000,
    timeRange.end * 1000,
    getO11yLokiRouteParams(clusterInfo)
  )
}
